/**
 * ActivityLogManager - Sprint 30
 *
 * Object-Oriented manager for device activity logs following OOAD principles.
 * Encapsulates all activity log state and operations in a single, cohesive class.
 *
 * Responsibilities:
 * - Load and cache activity logs from canister
 * - Render logs to DOM with proper formatting
 * - Handle pagination (next/prev/specific page)
 * - Filter logs by event type
 * - Log access events when devices are viewed
 *
 * Dependencies:
 * - CanisterClient for API communication (injected via constructor)
 * - DOM container elements (configured via IDs)
 */

// ============================================================================
// Types and Interfaces
// ============================================================================

/**
 * Event types for activity log entries
 */
export type ActivityEventType =
    | 'Discovery'
    | 'StatusChange'
    | 'ConfigChange'
    | 'InventoryUpdate'
    | 'RelationshipChange'
    | 'ManualEdit'
    | 'Access'
    | 'Export'
    | 'Alert'
    | 'System';

/**
 * Structure of an activity log entry from the canister
 */
export interface ActivityLogEntry {
    id: string;
    device_id: string;
    event_type: { [key in ActivityEventType]?: null };
    action: string;
    details: string;
    timestamp: number;
    source: string;
    user_principal: string[] | null;
    metadata: [string, string][];
}

/**
 * Configuration for ActivityLogManager
 */
export interface ActivityLogManagerConfig {
    /** ID of the container element for log entries */
    listContainerId: string;
    /** ID of the pagination container */
    paginationContainerId: string;
    /** ID of the filter select element */
    filterSelectId: string;
    /** ID for the activity count badge */
    countBadgeId?: string;
    /** Number of logs per page (default: 10) */
    pageSize?: number;
}

/**
 * Response structure from activity logs API
 */
interface ActivityLogsResponse {
    logs: ActivityLogEntry[];
    total_count: number;
}

// ============================================================================
// Event Type Mappings (Static Data)
// ============================================================================

/**
 * Human-readable names for event types
 */
const EVENT_TYPE_NAMES: Record<string, string> = {
    'Discovery': 'Discovery',
    'StatusChange': 'Status Change',
    'ConfigChange': 'Config Change',
    'InventoryUpdate': 'Inventory Update',
    'RelationshipChange': 'Relationship',
    'ManualEdit': 'Manual Edit',
    'Access': 'Access',
    'Export': 'Export',
    'Alert': 'Alert',
    'System': 'System'
};

/**
 * CSS classes for event type styling
 */
const EVENT_TYPE_CLASSES: Record<string, string> = {
    'Discovery': 'discovery',
    'Status Change': 'status-change',
    'Config Change': 'config-change',
    'Inventory Update': 'inventory-update',
    'Relationship': 'relationship-change',
    'Manual Edit': 'system',
    'Access': 'access',
    'Export': 'export',
    'Alert': 'alert',
    'System': 'system'
};

/**
 * Icons for event types
 */
const EVENT_TYPE_ICONS: Record<string, string> = {
    'Discovery': '&#x1F50D;',      // magnifying glass
    'Status Change': '&#x1F504;',  // arrows circle
    'Config Change': '&#x2699;',   // gear
    'Inventory Update': '&#x1F4E6;', // package
    'Relationship': '&#x1F517;',   // link
    'Manual Edit': '&#x270F;',     // pencil
    'Access': '&#x1F441;',         // eye
    'Export': '&#x1F4E4;',         // outbox
    'Alert': '&#x26A0;',           // warning
    'System': '&#x1F5A5;'          // desktop
};

// ============================================================================
// ActivityLogManager Class
// ============================================================================

/**
 * Manages activity logs for a device with proper OOP encapsulation.
 *
 * Usage:
 * ```typescript
 * const manager = new ActivityLogManager({
 *     listContainerId: 'activity-log-list',
 *     paginationContainerId: 'activity-pagination',
 *     filterSelectId: 'activity-filter'
 * }, canisterClient);
 *
 * await manager.loadLogs('device-uuid');
 * ```
 */
export class ActivityLogManager {
    // ========================================================================
    // Private State
    // ========================================================================

    /** Current device ID being viewed */
    private deviceId: string | null = null;

    /** Cached log entries for the current device */
    private logs: ActivityLogEntry[] = [];

    /** Current pagination offset */
    private offset: number = 0;

    /** Page size (number of logs per page) */
    private readonly limit: number;

    /** Total number of logs for current device */
    private total: number = 0;

    /** Current filter selection */
    private filter: ActivityEventType | 'all' = 'all';

    // ========================================================================
    // Configuration
    // ========================================================================

    private readonly config: Required<ActivityLogManagerConfig>;

    // ========================================================================
    // Dependencies
    // ========================================================================

    /** Canister client for API calls */
    private readonly canisterClient: any;

    // ========================================================================
    // Constructor
    // ========================================================================

    /**
     * Create a new ActivityLogManager instance
     * @param config - Configuration object with DOM element IDs
     * @param canisterClient - Canister client for API communication
     */
    constructor(config: ActivityLogManagerConfig, canisterClient: any) {
        this.config = {
            listContainerId: config.listContainerId,
            paginationContainerId: config.paginationContainerId,
            filterSelectId: config.filterSelectId,
            countBadgeId: config.countBadgeId || 'activity-count-badge',
            pageSize: config.pageSize || 10
        };
        this.limit = this.config.pageSize;
        this.canisterClient = canisterClient;

        this.bindEvents();
    }

    // ========================================================================
    // Public Methods - Main Interface
    // ========================================================================

    /**
     * Load activity logs for a device
     * @param deviceId - UUID of the device
     */
    public async loadLogs(deviceId: string): Promise<void> {
        this.deviceId = deviceId;
        this.offset = 0;
        this.filter = 'all';

        // Reset filter dropdown if it exists
        const filterSelect = this.getFilterSelect();
        if (filterSelect) {
            filterSelect.value = 'all';
        }

        await this.fetchAndRender();
    }

    /**
     * Refresh current logs (reload from server)
     */
    public async refresh(): Promise<void> {
        if (!this.deviceId) return;
        this.offset = 0;
        await this.fetchAndRender();
    }

    /**
     * Load activity log count for badge display
     * @param deviceId - UUID of the device
     */
    public async loadCount(deviceId: string): Promise<void> {
        try {
            if (!this.canisterClient) return;

            const result = await this.canisterClient.getDeviceActivityLogs(deviceId, 1, 0);
            const totalCount = this.extractTotalCount(result);

            this.updateCountBadge(totalCount);
        } catch (error) {
            console.warn('Failed to load activity count:', error);
        }
    }

    /**
     * Navigate to next page
     */
    public async nextPage(): Promise<void> {
        if (this.offset + this.limit >= this.total) return;
        this.offset += this.limit;
        await this.fetchAndRender();
    }

    /**
     * Navigate to previous page
     */
    public async prevPage(): Promise<void> {
        if (this.offset === 0) return;
        this.offset = Math.max(0, this.offset - this.limit);
        await this.fetchAndRender();
    }

    /**
     * Navigate to a specific page
     * @param page - Page number (1-indexed)
     */
    public async goToPage(page: number): Promise<void> {
        const newOffset = (page - 1) * this.limit;
        if (newOffset < 0 || newOffset >= this.total) return;
        this.offset = newOffset;
        await this.fetchAndRender();
    }

    /**
     * Apply event type filter
     * @param filter - Event type to filter by, or 'all' for no filter
     */
    public setFilter(filter: ActivityEventType | 'all'): void {
        this.filter = filter;
        this.renderLogs();
    }

    /**
     * Log a device access event
     * @param deviceId - UUID of the device being accessed
     */
    public async logAccess(deviceId: string): Promise<void> {
        try {
            if (!this.canisterClient) return;

            await this.canisterClient.addDeviceActivityLog({
                device_id: deviceId,
                event_type: { Access: null },
                action: 'Device details viewed',
                details: JSON.stringify({
                    view_type: 'modal',
                    timestamp: new Date().toISOString()
                }),
                user_principal: null,
                source: 'manual',
                metadata: []
            });
            console.log('✅ Access event logged for device:', deviceId);
        } catch (error) {
            console.warn('Failed to log access event:', error);
        }
    }

    /**
     * Clear state when modal is closed
     */
    public clear(): void {
        this.deviceId = null;
        this.logs = [];
        this.offset = 0;
        this.total = 0;
        this.filter = 'all';
    }

    /**
     * Get current device ID
     */
    public getDeviceId(): string | null {
        return this.deviceId;
    }

    // ========================================================================
    // Private Methods - Data Fetching
    // ========================================================================

    /**
     * Fetch logs from canister and render
     */
    private async fetchAndRender(): Promise<void> {
        if (!this.deviceId) return;

        const container = this.getListContainer();
        if (!container) return;

        this.showLoading(container);

        try {
            if (!this.canisterClient) {
                this.showError(container, 'Canister client not available');
                return;
            }

            const result = await this.canisterClient.getDeviceActivityLogs(
                this.deviceId,
                this.limit,
                this.offset
            );

            console.log('📋 Activity logs result:', result);

            // Extract logs from response (handle both formats)
            const logsData = this.extractLogsData(result);

            if (logsData) {
                this.logs = logsData.logs;
                this.total = logsData.total_count;
                this.renderLogs();
                this.renderPagination();
            } else if (result && result.Err) {
                this.showError(container, result.Err);
            } else {
                this.showEmpty(container);
            }
        } catch (error) {
            console.error('Failed to load activity logs:', error);
            this.showError(container, 'Failed to load activity logs');
        }
    }

    /**
     * Extract logs data from API response
     * Handles both { logs, total_count } and { Ok: { logs, total_count } } formats
     */
    private extractLogsData(result: any): ActivityLogsResponse | null {
        if (!result) return null;

        // Direct format (from canister-client extraction)
        if (result.logs && Array.isArray(result.logs)) {
            return {
                logs: result.logs,
                total_count: Number(result.total_count || 0)
            };
        }

        // Wrapped format (Result type)
        if (result.Ok && result.Ok.logs) {
            return {
                logs: result.Ok.logs,
                total_count: Number(result.Ok.total_count || 0)
            };
        }

        return null;
    }

    /**
     * Extract total count from API response
     */
    private extractTotalCount(result: any): number {
        if (!result) return 0;

        if (result.total_count !== undefined) {
            return Number(result.total_count);
        }
        if (result.Ok && result.Ok.total_count !== undefined) {
            return Number(result.Ok.total_count);
        }
        return 0;
    }

    // ========================================================================
    // Private Methods - Rendering
    // ========================================================================

    /**
     * Render activity logs to the container
     */
    private renderLogs(): void {
        const container = this.getListContainer();
        if (!container) return;

        // Apply filter if not 'all'
        const logsToRender = this.filter === 'all'
            ? this.logs
            : this.logs.filter(log => {
                const eventType = this.getEventTypeName(log.event_type);
                return eventType === this.filter ||
                       Object.keys(log.event_type || {})[0] === this.filter;
            });

        if (!logsToRender || logsToRender.length === 0) {
            this.showEmpty(container);
            return;
        }

        container.innerHTML = logsToRender.map(log => this.renderLogEntry(log)).join('');
    }

    /**
     * Render a single log entry
     */
    private renderLogEntry(log: ActivityLogEntry): string {
        const eventType = this.getEventTypeName(log.event_type);
        const eventClass = this.getEventTypeClass(eventType);
        const icon = this.getEventTypeIcon(eventType);
        const timestamp = this.formatTimestamp(log.timestamp);
        const source = log.source || 'system';

        // Parse details JSON if possible
        let detailsHtml = '';
        try {
            const details = JSON.parse(log.details);
            detailsHtml = Object.entries(details)
                .map(([key, value]) => `<span class="inline-block mr-3"><strong>${key}:</strong> ${value}</span>`)
                .join('');
        } catch {
            detailsHtml = this.escapeHtml(log.details);
        }

        const userPrincipal = log.user_principal && log.user_principal[0]
            ? ` &bull; User: ${this.escapeHtml(log.user_principal[0])}`
            : '';

        return `
            <div class="activity-log-entry border rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                 onclick="this.classList.toggle('expanded')">
                <div class="flex items-start justify-between">
                    <div class="flex items-center space-x-3">
                        <span class="activity-icon ${eventClass} w-8 h-8 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0">
                            ${icon}
                        </span>
                        <div>
                            <p class="font-medium text-gray-900">${this.escapeHtml(log.action)}</p>
                            <p class="text-xs text-gray-500">Source: ${this.escapeHtml(source)}${userPrincipal}</p>
                        </div>
                    </div>
                    <div class="text-right flex-shrink-0">
                        <p class="text-sm text-gray-600">${timestamp}</p>
                        <span class="type-badge-${eventClass} px-2 py-0.5 text-xs rounded-full">${eventType}</span>
                    </div>
                </div>
                <div class="activity-details mt-2 pt-2 border-t text-sm text-gray-600 hidden">
                    ${detailsHtml || 'No additional details'}
                </div>
            </div>
        `;
    }

    /**
     * Render pagination controls
     */
    private renderPagination(): void {
        const pagination = document.getElementById(this.config.paginationContainerId);
        if (!pagination) return;

        const showingStart = document.getElementById('activity-showing-start');
        const showingEnd = document.getElementById('activity-showing-end');
        const totalSpan = document.getElementById('activity-total');
        const prevBtn = document.getElementById('activity-prev-btn') as HTMLButtonElement;
        const nextBtn = document.getElementById('activity-next-btn') as HTMLButtonElement;

        // Update numbers
        const startNum = this.total > 0 ? this.offset + 1 : 0;
        const endNum = Math.min(this.offset + this.limit, this.total);

        if (showingStart) showingStart.textContent = startNum.toString();
        if (showingEnd) showingEnd.textContent = endNum.toString();
        if (totalSpan) totalSpan.textContent = this.total.toString();

        // Show/hide pagination
        if (this.total === 0) {
            pagination.classList.add('hidden');
            return;
        }

        pagination.classList.remove('hidden');

        // Enable/disable navigation buttons
        const hasMultiplePages = this.total > this.limit;
        if (prevBtn) {
            prevBtn.classList.toggle('hidden', !hasMultiplePages);
            prevBtn.disabled = this.offset === 0;
        }
        if (nextBtn) {
            nextBtn.classList.toggle('hidden', !hasMultiplePages);
            nextBtn.disabled = this.offset + this.limit >= this.total;
        }
    }

    /**
     * Update the activity count badge
     */
    private updateCountBadge(count: number): void {
        const badge = document.getElementById(this.config.countBadgeId);
        if (badge) {
            badge.textContent = count.toString();
            badge.classList.toggle('hidden', count === 0);
        }
    }

    // ========================================================================
    // Private Methods - UI States
    // ========================================================================

    /**
     * Show loading indicator
     */
    private showLoading(container: HTMLElement): void {
        container.innerHTML = `
            <div class="flex items-center justify-center py-8 text-gray-500">
                <svg class="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading activity history...
            </div>
        `;
    }

    /**
     * Show error message
     */
    private showError(container: HTMLElement, message: string): void {
        container.innerHTML = `<p class="text-center text-red-500 py-4">Error: ${this.escapeHtml(message)}</p>`;
    }

    /**
     * Show empty state
     */
    private showEmpty(container: HTMLElement): void {
        container.innerHTML = '<p class="text-center text-gray-500 py-4">No activity logs found</p>';
    }

    // ========================================================================
    // Private Methods - Event Binding
    // ========================================================================

    /**
     * Bind event handlers
     */
    private bindEvents(): void {
        // Filter change handler
        const filterSelect = this.getFilterSelect();
        if (filterSelect) {
            filterSelect.addEventListener('change', () => {
                this.setFilter(filterSelect.value as ActivityEventType | 'all');
            });
        }
    }

    // ========================================================================
    // Private Methods - DOM Access
    // ========================================================================

    /**
     * Get the log list container element
     */
    private getListContainer(): HTMLElement | null {
        return document.getElementById(this.config.listContainerId);
    }

    /**
     * Get the filter select element
     */
    private getFilterSelect(): HTMLSelectElement | null {
        return document.getElementById(this.config.filterSelectId) as HTMLSelectElement;
    }

    // ========================================================================
    // Private Methods - Formatting Helpers
    // ========================================================================

    /**
     * Get human-readable event type name
     */
    private getEventTypeName(eventType: any): string {
        if (!eventType) return 'Unknown';
        const key = Object.keys(eventType)[0];
        return EVENT_TYPE_NAMES[key] || key || 'Unknown';
    }

    /**
     * Get CSS class for event type
     */
    private getEventTypeClass(eventType: string): string {
        return EVENT_TYPE_CLASSES[eventType] || 'system';
    }

    /**
     * Get icon for event type
     */
    private getEventTypeIcon(eventType: string): string {
        return EVENT_TYPE_ICONS[eventType] || '&#x1F4CB;'; // clipboard
    }

    /**
     * Format timestamp for display
     */
    private formatTimestamp(timestamp: bigint | number): string {
        try {
            // Convert nanoseconds to milliseconds
            const ms = Number(timestamp) / 1_000_000;
            const date = new Date(ms);

            if (isNaN(date.getTime())) {
                return 'Unknown';
            }

            const now = new Date();
            const diff = now.getTime() - date.getTime();

            // Less than 24 hours: relative time
            if (diff < 86400000) {
                if (diff < 60000) return 'Just now';
                if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                return `${Math.floor(diff / 3600000)}h ago`;
            }

            // Less than 7 days: day and time
            if (diff < 604800000) {
                return date.toLocaleDateString('en-AU', {
                    weekday: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }

            // Otherwise: full date
            return date.toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Unknown';
        }
    }

    /**
     * Escape HTML to prevent XSS
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

// ============================================================================
// Factory Function for Easy Instantiation
// ============================================================================

/**
 * Create an ActivityLogManager with default configuration
 * @param canisterClient - Canister client for API calls
 */
export function createActivityLogManager(canisterClient: any): ActivityLogManager {
    return new ActivityLogManager({
        listContainerId: 'activity-log-list',
        paginationContainerId: 'activity-pagination',
        filterSelectId: 'activity-filter',
        countBadgeId: 'activity-count-badge',
        pageSize: 10
    }, canisterClient);
}
